import { useState, useEffect } from "react";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
  DialogFooter,
} from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { Card, CardContent } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Loader2, FileText, Check, AlertTriangle, XCircle } from "lucide-react";
import { ScrollArea } from "@/components/ui/scroll-area";

export interface PendingDocument {
  fileName: string;
  category: string;
  description: string;
  confidence: number;
  file: File;
}

interface DocumentCategoryConfirmDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  pendingDocuments: PendingDocument[];
  categories: string[];
  onConfirm: (documents: PendingDocument[]) => void;
  isLoading?: boolean;
}

export function DocumentCategoryConfirmDialog({
  open,
  onOpenChange,
  pendingDocuments,
  categories,
  onConfirm,
  isLoading = false,
}: DocumentCategoryConfirmDialogProps) {
  const [editedDocuments, setEditedDocuments] = useState<PendingDocument[]>([]);

  // Initialize edited documents when pendingDocuments change
  useEffect(() => {
    if (pendingDocuments.length > 0) {
      setEditedDocuments(pendingDocuments.map(doc => ({ ...doc })));
    }
  }, [pendingDocuments]);

  const handleCategoryChange = (index: number, category: string) => {
    setEditedDocuments(prev => {
      const updated = [...prev];
      updated[index] = { ...updated[index], category };
      return updated;
    });
  };

  const handleDescriptionChange = (index: number, description: string) => {
    setEditedDocuments(prev => {
      const updated = [...prev];
      updated[index] = { ...updated[index], description };
      return updated;
    });
  };

  const getConfidenceIndicator = (confidence: number) => {
    if (confidence >= 0.8) {
      return {
        icon: <Check className="h-4 w-4" />,
        color: "text-green-600 dark:text-green-400",
        bgColor: "bg-green-100 dark:bg-green-900/30",
        label: "Hohe Sicherheit",
      };
    } else if (confidence >= 0.5) {
      return {
        icon: <AlertTriangle className="h-4 w-4" />,
        color: "text-yellow-600 dark:text-yellow-400",
        bgColor: "bg-yellow-100 dark:bg-yellow-900/30",
        label: "Mittlere Sicherheit",
      };
    } else {
      return {
        icon: <XCircle className="h-4 w-4" />,
        color: "text-red-600 dark:text-red-400",
        bgColor: "bg-red-100 dark:bg-red-900/30",
        label: "Niedrige Sicherheit",
      };
    }
  };

  const handleConfirm = () => {
    onConfirm(editedDocuments);
  };

  const handleCancel = () => {
    onOpenChange(false);
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-2xl max-h-[90vh] flex flex-col">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <FileText className="h-5 w-5" />
            Dokumente kategorisieren
          </DialogTitle>
          <DialogDescription>
            Die KI konnte folgende Dokumente nicht sicher kategorisieren. 
            Bitte überprüfen Sie die Zuordnung:
          </DialogDescription>
        </DialogHeader>

        <ScrollArea className="flex-1 max-h-[50vh] pr-4">
          <div className="space-y-4">
            {editedDocuments.map((doc, index) => {
              const confidence = getConfidenceIndicator(doc.confidence);
              return (
                <Card key={`${doc.fileName}-${index}`} className="overflow-hidden">
                  <CardContent className="p-4 space-y-3">
                    {/* File name and confidence */}
                    <div className="flex items-start justify-between gap-2">
                      <div className="flex items-center gap-2 min-w-0">
                        <FileText className="h-5 w-5 text-muted-foreground shrink-0" />
                        <span className="font-medium truncate" title={doc.fileName}>
                          {doc.fileName}
                        </span>
                      </div>
                      <Badge
                        variant="outline"
                        className={`${confidence.bgColor} ${confidence.color} shrink-0 flex items-center gap-1`}
                      >
                        {confidence.icon}
                        <span className="text-xs">{Math.round(doc.confidence * 100)}%</span>
                      </Badge>
                    </div>

                    {/* AI suggestion */}
                    <div className={`text-sm px-2 py-1 rounded ${confidence.bgColor} ${confidence.color}`}>
                      <span className="opacity-80">KI-Vorschlag:</span>{" "}
                      <span className="font-medium">{pendingDocuments[index]?.category || "Sonstiges"}</span>
                    </div>

                    {/* Category selection */}
                    <div className="grid gap-3 sm:grid-cols-2">
                      <div className="space-y-1.5">
                        <Label htmlFor={`category-${index}`} className="text-sm">
                          Kategorie
                        </Label>
                        <Select
                          value={doc.category}
                          onValueChange={(value) => handleCategoryChange(index, value)}
                        >
                          <SelectTrigger id={`category-${index}`}>
                            <SelectValue />
                          </SelectTrigger>
                          <SelectContent>
                            {categories.map((cat) => (
                              <SelectItem key={cat} value={cat}>
                                {cat}
                              </SelectItem>
                            ))}
                          </SelectContent>
                        </Select>
                      </div>
                      <div className="space-y-1.5">
                        <Label htmlFor={`description-${index}`} className="text-sm">
                          Beschreibung (optional)
                        </Label>
                        <Input
                          id={`description-${index}`}
                          value={doc.description}
                          onChange={(e) => handleDescriptionChange(index, e.target.value)}
                          placeholder="z.B. Schulungszertifikat 2024"
                        />
                      </div>
                    </div>
                  </CardContent>
                </Card>
              );
            })}
          </div>
        </ScrollArea>

        <DialogFooter className="mt-4">
          <Button variant="outline" onClick={handleCancel} disabled={isLoading}>
            Abbrechen
          </Button>
          <Button onClick={handleConfirm} disabled={isLoading}>
            {isLoading ? (
              <>
                <Loader2 className="h-4 w-4 animate-spin mr-2" />
                Speichern...
              </>
            ) : (
              `${editedDocuments.length} Dokument${editedDocuments.length !== 1 ? "e" : ""} speichern`
            )}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
